/**
 * Daily Digest Studio API client.
 *
 * Talks to the NEW /admin/morning-brief/studio/* endpoints on the chat module
 * (same auth + base-url pattern as morningBriefAdminApi.js). These are separate
 * from the existing morning-brief endpoints and operate on an isolated studio
 * copy, so the live digest is never affected.
 */

function getChatBaseUrl() {
    const override = localStorage.getItem('chatBaseUrl');
    if (override) return override;
    const env = localStorage.getItem('env') === 'true';
    return env
        ? 'https://prod-chat-module-api.dugoutlive.com'
        : 'https://chat-module-api.dugoutlive.com';
}

function getAuthHeaders() {
    const token = sessionStorage.getItem('sessionToken');
    if (!token) return {};
    return { Authorization: `Bearer ${token}`, auth: token };
}

async function handleResponse(res) {
    if (!res.ok) {
        let errorMsg = `Request failed: ${res.status}`;
        try {
            const body = await res.json();
            errorMsg = body.message || body.error || errorMsg;
        } catch { /* ignore */ }
        throw new Error(errorMsg);
    }
    return res.json();
}

const base = () => `${getChatBaseUrl()}/admin/morning-brief/studio`;

export async function listDigestDays(limit = 30) {
    const res = await fetch(`${base()}/days?limit=${limit}`, { credentials: 'include', headers: getAuthHeaders() });
    return handleResponse(res);
}

export async function getDigestDay(dayKey) {
    const res = await fetch(`${base()}/day/${encodeURIComponent(dayKey)}`, { credentials: 'include', headers: getAuthHeaders() });
    return handleResponse(res);
}

export async function updateDigestCard(dayKey, cardId, patch) {
    const res = await fetch(`${base()}/day/${encodeURIComponent(dayKey)}/card/${cardId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
    });
    return handleResponse(res);
}

// cardId omitted → render every card for the day.
export async function renderDigestDay(dayKey, cardId = null) {
    const res = await fetch(`${base()}/day/${encodeURIComponent(dayKey)}/render`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(cardId != null ? { cardId } : {}),
    });
    return handleResponse(res);
}

export async function regenerateDigestDay(dayKey) {
    const res = await fetch(`${base()}/day/${encodeURIComponent(dayKey)}/regenerate`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    });
    return handleResponse(res);
}

// Upload an image file (player/team photo) → S3 via presigned PUT → returns the
// final CDN image_url to store on the player.
export async function uploadDigestImage(file) {
    const contentType = file.type || 'image/png';
    const ext = (file.name?.split('.').pop() || contentType.split('/')[1] || 'png');
    const res = await fetch(`${base()}/upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType, ext }),
    });
    const { data } = await handleResponse(res);
    const put = await fetch(data.upload_url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
    if (!put.ok) throw new Error(`Upload failed: ${put.status}`);
    return data.image_url;
}
